// Growlab 811 — 3D equipment + environment parts. Each component stays small;
// motion is driven by the `running` prop so the bottom bar can pause the model.
import React, { useRef, useMemo } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { Html } from "@react-three/drei";
import { SOIL_BASE } from "./data";

const METAL = { color: "#475569", metalness: 0.85, roughness: 0.35 };

export function DeviceLabel({ id, name, selected, onSelect, y = 1.4 }) {
  return (
    <Html position={[0, y, 0]} center distanceFactor={11} zIndexRange={[20, 0]}>
      <button
        data-testid={`label-${id}`}
        onClick={(e) => { e.stopPropagation(); onSelect(id); }}
        className={`px-2 py-1 rounded font-mono text-[10px] tracking-widest whitespace-nowrap border transition-colors duration-200 ${
          selected
            ? "bg-amber-400 text-stone-950 border-amber-300"
            : "bg-stone-950/85 text-amber-300/90 border-amber-500/30 hover:border-amber-400"
        }`}
      >
        {name}
      </button>
    </Html>
  );
}

export function SpinRing({ color = "#e5c158" }) {
  const ref = useRef();
  useFrame((_, d) => { if (ref.current) ref.current.rotation.y += d * 1.4; });
  return (
    <mesh ref={ref} rotation={[Math.PI / 2, 0, 0]}>
      <torusGeometry args={[1.25, 0.035, 8, 48]} />
      <meshBasicMaterial color={color} transparent opacity={0.9} />
    </mesh>
  );
}

function Blades({ radius = 0.42, speed = 16, running, tint = "#94a3b8" }) {
  const ref = useRef();
  useFrame((_, d) => { if (running && ref.current) ref.current.rotation.z += d * speed; });
  return (
    <group ref={ref}>
      {[0, 1, 2, 3].map((i) => (
        <mesh key={i} rotation={[0.5, 0, (i * Math.PI) / 2]} position={0 ? undefined : undefined}>
          <boxGeometry args={[radius, 0.14, 0.03]} />
          <meshStandardMaterial color={tint} metalness={0.6} roughness={0.4} />
        </mesh>
      ))}
    </group>
  );
}

function Duct({ length, radius = 0.34, axis = "y", offset = [0, 0, 0] }) {
  const rot = axis === "y" ? [0, 0, 0] : axis === "z" ? [Math.PI / 2, 0, 0] : [0, 0, Math.PI / 2];
  return (
    <mesh position={offset} rotation={rot}>
      <cylinderGeometry args={[radius, radius, length, 20, 1, true]} />
      <meshStandardMaterial {...METAL} side={2} />
    </mesh>
  );
}

export function T8Unit({ running }) {
  return (
    <group>
      <mesh><cylinderGeometry args={[0.6, 0.6, 1.3, 24, 1, true]} /><meshStandardMaterial {...METAL} side={2} /></mesh>
      <mesh position={[0, -0.68, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.6, 0.06, 10, 32]} /><meshStandardMaterial color="#f97316" emissive="#f97316" emissiveIntensity={0.7} />
      </mesh>
      <group rotation={[Math.PI / 2, 0, 0]}><Blades running={running} speed={22} /></group>
      <Duct length={1.6} offset={[0, 1.35, 0]} />
    </group>
  );
}

export function RaxialUnit({ running }) {
  return (
    <group>
      <mesh rotation={[Math.PI / 2, 0, 0]}><cylinderGeometry args={[0.5, 0.5, 1.1, 24, 1, true]} /><meshStandardMaterial {...METAL} side={2} /></mesh>
      <mesh position={[0, 0, -0.6]}><torusGeometry args={[0.5, 0.05, 10, 32]} /><meshStandardMaterial color="#06b6d4" emissive="#06b6d4" emissiveIntensity={0.7} /></mesh>
      <Blades running={running} speed={18} />
      <Duct length={1.4} radius={0.3} axis="z" offset={[0, 0, 1.2]} />
    </group>
  );
}

export function CloudrayUnit({ running }) {
  const head = useRef();
  useFrame(({ clock }) => {
    if (head.current) head.current.rotation.y = running ? Math.sin(clock.getElapsedTime() * 0.7) * 0.75 : head.current.rotation.y;
  });
  return (
    <group>
      <mesh position={[0.35, 0, 0]}><boxGeometry args={[0.35, 0.7, 0.5]} /><meshStandardMaterial {...METAL} /></mesh>
      <group ref={head}>
        <mesh position={[-0.4, 0, 0]} rotation={[0, 0, Math.PI / 2]}><cylinderGeometry args={[0.12, 0.12, 0.7, 12]} /><meshStandardMaterial {...METAL} /></mesh>
        <group position={[-0.8, 0, 0]} rotation={[0, Math.PI / 2, 0]}>
          <mesh><torusGeometry args={[0.55, 0.05, 10, 32]} /><meshStandardMaterial color="#e5c158" emissive="#e5c158" emissiveIntensity={0.5} /></mesh>
          <Blades running={running} speed={12} radius={0.5} tint="#e2e8f0" />
        </group>
      </group>
    </group>
  );
}

export function CloudforgeUnit() {
  return (
    <group>
      <mesh position={[0, 0.7, 0]}><boxGeometry args={[1.1, 1.5, 1.1]} /><meshStandardMaterial color="#1f2937" metalness={0.4} roughness={0.6} /></mesh>
      <mesh position={[0, 0.7, 0.56]}><boxGeometry args={[0.9, 0.12, 0.02]} /><meshStandardMaterial color="#7dd3fc" emissive="#7dd3fc" emissiveIntensity={0.9} /></mesh>
      <mesh position={[0, 1.55, 0]}><cylinderGeometry args={[0.16, 0.22, 0.35, 16]} /><meshStandardMaterial {...METAL} /></mesh>
    </group>
  );
}

export function ControllerUnit() {
  return (
    <group rotation={[0, -0.6, 0]}>
      <mesh><boxGeometry args={[1.5, 2.1, 0.28]} /><meshStandardMaterial color="#111827" metalness={0.5} roughness={0.5} /></mesh>
      <mesh position={[0, 0.25, 0.15]}><planeGeometry args={[1.2, 1.1]} /><meshStandardMaterial color="#0c0a09" emissive="#e5c158" emissiveIntensity={0.55} /></mesh>
      {[-0.3, -0.55, -0.8].map((y, i) => (
        <mesh key={y} position={[0, y, 0.15]}><boxGeometry args={[1.1 - i * 0.25, 0.07, 0.02]} /><meshStandardMaterial color="#38bdf8" emissive="#38bdf8" emissiveIntensity={0.8} /></mesh>
      ))}
    </group>
  );
}

export function OutletUnit() {
  const led = useRef();
  useFrame(({ clock }) => {
    if (led.current) led.current.material.emissiveIntensity = 0.7 + Math.sin(clock.getElapsedTime() * 3) * 0.4;
  });
  return (
    <group rotation={[0, -0.6, 0]}>
      <mesh><boxGeometry args={[0.8, 1.1, 0.45]} /><meshStandardMaterial color="#1f2937" metalness={0.4} roughness={0.6} /></mesh>
      <mesh ref={led} position={[0, 0.3, 0.24]}><sphereGeometry args={[0.09, 12, 12]} /><meshStandardMaterial color="#10b981" emissive="#10b981" emissiveIntensity={1} /></mesh>
    </group>
  );
}

export function Tree({ tree, running }) {
  const sway = useRef();
  useFrame(({ clock }) => {
    if (running && sway.current) sway.current.rotation.z = Math.sin(clock.getElapsedTime() * 0.9 + tree.pos[0]) * 0.025;
  });
  const top = SOIL_BASE + tree.h;
  const blobs = [[0, 0, 0], [0.6, -0.25, 0.3], [-0.55, -0.2, -0.25], [0.1, -0.3, 0.55], [-0.2, -0.15, -0.6]];
  return (
    <group position={[tree.pos[0], SOIL_BASE, tree.pos[2]]}>
      <group ref={sway}>
        <mesh position={[0, tree.h * 0.35, 0]}><cylinderGeometry args={[0.09, 0.16, tree.h * 0.7, 8]} /><meshStandardMaterial color="#7c5a3a" roughness={0.9} /></mesh>
        <group position={[0, tree.h * 0.72, 0]}>
          {blobs.map((p, i) => (
            <mesh key={i} position={p} scale={[1, 0.72, 1]}>
              <sphereGeometry args={[tree.canopy * (i === 0 ? 1 : 0.62), 10, 8]} />
              <meshStandardMaterial color={tree.leaf} roughness={0.85} />
            </mesh>
          ))}
        </group>
      </group>
      <Html position={[0, top - SOIL_BASE + 0.4, 0]} center distanceFactor={13} zIndexRange={[5, 0]}>
        <span className="font-mono text-[9px] tracking-widest text-emerald-300/80 bg-stone-950/70 px-1.5 py-0.5 rounded border border-emerald-500/20 whitespace-nowrap">
          {tree.name}
        </span>
      </Html>
    </group>
  );
}

export function Tent({ showWalls }) {
  const wall = { color: "#334155", metalness: 0.85, roughness: 0.3, transparent: true, side: 2 };
  const pole = { color: "#64748b", metalness: 0.9, roughness: 0.3 };
  const corners = [[-5, -5], [5, -5], [-5, 5], [5, 5]];
  return (
    <group>
      <mesh position={[0, 0, -5]}><planeGeometry args={[10, 9]} /><meshStandardMaterial {...wall} opacity={showWalls ? 0.22 : 0.07} /></mesh>
      <mesh position={[-5, 0, 0]} rotation={[0, Math.PI / 2, 0]}><planeGeometry args={[10, 9]} /><meshStandardMaterial {...wall} opacity={showWalls ? 0.22 : 0.07} /></mesh>
      <mesh position={[5, 0, 0]} rotation={[0, -Math.PI / 2, 0]}><planeGeometry args={[10, 9]} /><meshStandardMaterial {...wall} opacity={showWalls ? 0.22 : 0.07} /></mesh>
      <mesh position={[0, 4.5, 0]} rotation={[Math.PI / 2, 0, 0]}><planeGeometry args={[10, 10]} /><meshStandardMaterial {...wall} opacity={showWalls ? 0.18 : 0.05} /></mesh>
      {showWalls && (
        <mesh position={[0, 0, 5]} rotation={[0, Math.PI, 0]}><planeGeometry args={[10, 9]} /><meshStandardMaterial {...wall} opacity={0.1} /></mesh>
      )}
      {corners.map(([x, z]) => (
        <mesh key={`${x}${z}`} position={[x, 0, z]}><cylinderGeometry args={[0.07, 0.07, 9, 8]} /><meshStandardMaterial {...pole} /></mesh>
      ))}
      {[[-5, -5, 5, -5], [5, -5, 5, 5], [5, 5, -5, 5], [-5, 5, -5, -5]].map((e, i) => {
        const cx = (e[0] + e[2]) / 2, cz = (e[1] + e[3]) / 2;
        const horiz = e[1] === e[3];
        return <mesh key={i} position={[cx, 4.5, cz]} rotation={horiz ? [0, 0, Math.PI / 2] : [Math.PI / 2, 0, 0]}><cylinderGeometry args={[0.06, 0.06, 10, 8]} /><meshStandardMaterial {...pole} /></mesh>;
      })}
    </group>
  );
}

// ETFE retrofit state (final spec): only TWO zipper-door walls (front + right) are cut open and
// fitted with framed 92-inch ETFE film; the ROOF gets the same cut + framed film treatment;
// back and left walls stay original fabric. Sliding pole hooks carry the frames.
function RetrofitWall({ position, rotationY = 0 }) {
  const fabric = { color: "#1a2433", transparent: true, opacity: 0.55, roughness: 0.9, side: 2 };
  const film = { color: "#dbeafe", transparent: true, opacity: 0.1, roughness: 0.12, metalness: 0.15, side: 2, depthWrite: false };
  const frame = { color: "#0b0b0d", metalness: 0.7, roughness: 0.4 };
  const OW = 8.5, OH = 6.4, OY = 0.2; // 92-inch film ≈ nearly full wall width
  return (
    <group position={position} rotation={[0, rotationY, 0]}>
      <mesh position={[-4.625, 0, 0]}><planeGeometry args={[0.75, 9]} /><meshStandardMaterial {...fabric} /></mesh>
      <mesh position={[4.625, 0, 0]}><planeGeometry args={[0.75, 9]} /><meshStandardMaterial {...fabric} /></mesh>
      <mesh position={[0, OY + OH / 2 + 0.65, 0]}><planeGeometry args={[OW, 1.3]} /><meshStandardMaterial {...fabric} /></mesh>
      <mesh position={[0, OY - OH / 2 - 0.65, 0]}><planeGeometry args={[OW, 1.3]} /><meshStandardMaterial {...fabric} /></mesh>
      <mesh position={[0, OY, 0.01]}><planeGeometry args={[OW, OH]} /><meshStandardMaterial {...film} /></mesh>
      <mesh position={[-OW / 2, OY, 0.03]}><boxGeometry args={[0.12, OH + 0.12, 0.12]} /><meshStandardMaterial {...frame} /></mesh>
      <mesh position={[OW / 2, OY, 0.03]}><boxGeometry args={[0.12, OH + 0.12, 0.12]} /><meshStandardMaterial {...frame} /></mesh>
      <mesh position={[0, OY + OH / 2, 0.03]}><boxGeometry args={[OW + 0.12, 0.12, 0.12]} /><meshStandardMaterial {...frame} /></mesh>
      <mesh position={[0, OY - OH / 2, 0.03]}><boxGeometry args={[OW + 0.12, 0.12, 0.12]} /><meshStandardMaterial {...frame} /></mesh>
    </group>
  );
}

function FabricWall({ position, rotationY = 0 }) {
  return (
    <mesh position={position} rotation={[0, rotationY, 0]}>
      <planeGeometry args={[10, 9]} />
      <meshStandardMaterial color="#1a2433" transparent opacity={0.55} roughness={0.9} side={2} />
    </mesh>
  );
}

function RetrofitRoof() {
  const fabric = { color: "#1a2433", transparent: true, opacity: 0.55, roughness: 0.9, side: 2 };
  const film = { color: "#dbeafe", transparent: true, opacity: 0.12, roughness: 0.12, metalness: 0.15, side: 2, depthWrite: false };
  const frame = { color: "#0b0b0d", metalness: 0.7, roughness: 0.4 };
  const OW = 8.5, OD = 7; // 92-inch film across the roof
  return (
    <group position={[0, 4.5, 0]}>
      <mesh position={[-4.625, 0, 0]} rotation={[Math.PI / 2, 0, 0]}><planeGeometry args={[0.75, 10]} /><meshStandardMaterial {...fabric} /></mesh>
      <mesh position={[4.625, 0, 0]} rotation={[Math.PI / 2, 0, 0]}><planeGeometry args={[0.75, 10]} /><meshStandardMaterial {...fabric} /></mesh>
      <mesh position={[0, 0, -4.25]} rotation={[Math.PI / 2, 0, 0]}><planeGeometry args={[10, 1.5]} /><meshStandardMaterial {...fabric} /></mesh>
      <mesh position={[0, 0, 4.25]} rotation={[Math.PI / 2, 0, 0]}><planeGeometry args={[10, 1.5]} /><meshStandardMaterial {...fabric} /></mesh>
      <mesh position={[0, 0.01, 0]} rotation={[Math.PI / 2, 0, 0]}><planeGeometry args={[OW, OD]} /><meshStandardMaterial {...film} /></mesh>
      <mesh position={[-OW / 2, 0.03, 0]}><boxGeometry args={[0.12, 0.12, OD + 0.12]} /><meshStandardMaterial {...frame} /></mesh>
      <mesh position={[OW / 2, 0.03, 0]}><boxGeometry args={[0.12, 0.12, OD + 0.12]} /><meshStandardMaterial {...frame} /></mesh>
      <mesh position={[0, 0.03, -OD / 2]}><boxGeometry args={[OW + 0.12, 0.12, 0.12]} /><meshStandardMaterial {...frame} /></mesh>
      <mesh position={[0, 0.03, OD / 2]}><boxGeometry args={[OW + 0.12, 0.12, 0.12]} /><meshStandardMaterial {...frame} /></mesh>
      {/* sunlight shafts falling through the roof film */}
      <mesh position={[-1.2, -2.2, 0.6]} rotation={[0.12, 0, 0.16]}>
        <boxGeometry args={[2.6, 4.6, 1.8]} />
        <meshBasicMaterial color="#ffd88a" transparent opacity={0.05} depthWrite={false} />
      </mesh>
      <mesh position={[1.6, -2.2, -0.8]} rotation={[-0.1, 0, -0.13]}>
        <boxGeometry args={[2.0, 4.6, 1.5]} />
        <meshBasicMaterial color="#ffe9b8" transparent opacity={0.04} depthWrite={false} />
      </mesh>
      <pointLight position={[0, -0.4, 0]} intensity={14} distance={12} color="#ffdf9e" />
    </group>
  );
}

export function ETFEShell({ visible }) {
  if (!visible) return null;
  return (
    <group>
      <RetrofitWall position={[0, 0, 5.02]} />
      <RetrofitWall position={[5.02, 0, 0]} rotationY={-Math.PI / 2} />
      <FabricWall position={[0, 0, -5.02]} rotationY={Math.PI} />
      <FabricWall position={[-5.02, 0, 0]} rotationY={Math.PI / 2} />
      <RetrofitRoof />
    </group>
  );
}

export function SoilBed() {
  return (
    <group>
      <mesh position={[0, -4.25, 0]}><boxGeometry args={[9.4, 0.5, 9.4]} /><meshStandardMaterial color="#44403c" roughness={1} /></mesh>
      <mesh position={[0, -3.75, 0]}><boxGeometry args={[9.4, 0.5, 9.4]} /><meshStandardMaterial color="#3b2f23" roughness={1} /></mesh>
      <mesh position={[0, -3.35, 0]}><boxGeometry args={[9.4, 0.3, 9.4]} /><meshStandardMaterial color="#6b5b45" roughness={1} /></mesh>
    </group>
  );
}

// Slim fill light only — the fixtures below carry the real light now.
// Sliding hooks CLAMP the tent's steel poles (block wraps the pole, through-bolt visible)
// and CARRY the framed ETFE film walls — straps run from each hook to the frame rails.
function HookMesh({ position }) {
  return (
    <group position={position}>
      {/* clamp block wrapping the pole */}
      <mesh><boxGeometry args={[0.24, 0.16, 0.28]} /><meshStandardMaterial color="#111113" metalness={0.6} roughness={0.4} /></mesh>
      {/* through-bolt */}
      <mesh rotation={[Math.PI / 2, 0, 0]}><cylinderGeometry args={[0.035, 0.035, 0.34, 10]} /><meshStandardMaterial color="#3f3f46" metalness={0.8} roughness={0.35} /></mesh>
      {/* hook joined to the block underside */}
      <mesh position={[0, -0.14, 0]} rotation={[0, 0, Math.PI / 2 + 0.5]}>
        <torusGeometry args={[0.17, 0.038, 8, 20, Math.PI * 1.35]} />
        <meshStandardMaterial color="#111113" metalness={0.6} roughness={0.4} />
      </mesh>
    </group>
  );
}

export function FrameHooks({ shellOn }) {
  // two hooks per corner pole (upper + lower), clamped ON the pole at y=2.1 / -1.5
  const corners = [[-5, -5], [5, -5], [-5, 5], [5, 5]];
  const hooks = [];
  corners.forEach(([x, z]) => [2.1, -1.5].forEach((y) => hooks.push([x, y, z])));
  const straps = useMemo(() => {
    // hooks -> film frame vertical rails, straight and level (rail x=±4.25 front / z=±4.25 right)
    const pairs = [
      [[-5, 2.1, 5], [-4.25, 2.1, 5.05]], [[-5, -1.5, 5], [-4.25, -1.5, 5.05]],
      [[5, 2.1, 5], [4.25, 2.1, 5.05]], [[5, -1.5, 5], [4.25, -1.5, 5.05]],
      [[5, 2.1, -5], [5.05, 2.1, -4.25]], [[5, -1.5, -5], [5.05, -1.5, -4.25]],
    ];
    return pairs.map(([a, b]) => new THREE.LineCurve3(new THREE.Vector3(...a), new THREE.Vector3(...b)));
  }, []);
  return (
    <group>
      {hooks.map((p, i) => <HookMesh key={i} position={p} />)}
      {shellOn && straps.map((c, i) => (
        <mesh key={i}>
          <tubeGeometry args={[c, 1, 0.028, 6]} />
          <meshStandardMaterial color="#292524" roughness={0.8} />
        </mesh>
      ))}
    </group>
  );
}

export function GrowLights() {
  return <pointLight position={[0, 2, 3]} intensity={8} distance={14} color="#ffd88a" />;
}

function HangWires({ spread = 1.0, top = 0.8 }) {
  return (
    <group>
      {[-1, 1].map((s) => (
        <mesh key={s} position={[s * spread, top / 2 + 0.25, 0]}>
          <cylinderGeometry args={[0.015, 0.015, top - 0.1, 6]} />
          <meshStandardMaterial color="#64748b" metalness={0.8} roughness={0.4} />
        </mesh>
      ))}
    </group>
  );
}

export function MHFixture() {
  return (
    <group>
      <mesh><boxGeometry args={[2.6, 0.5, 1.8]} /><meshStandardMaterial color="#1f2937" metalness={0.7} roughness={0.35} /></mesh>
      <mesh position={[0, -0.3, 0]} rotation={[0, 0, Math.PI / 2]}><cylinderGeometry args={[0.13, 0.13, 1.3, 12]} /><meshStandardMaterial color="#e0f2fe" emissive="#bae6fd" emissiveIntensity={2.4} /></mesh>
      <mesh position={[0, -2.0, 0]}><coneGeometry args={[2.0, 3.2, 24, 1, true]} /><meshBasicMaterial color="#bae6fd" transparent opacity={0.06} side={2} depthWrite={false} /></mesh>
      <HangWires spread={1.0} />
      <pointLight position={[0, -1.4, 0]} intensity={26} distance={13} color="#bae6fd" />
    </group>
  );
}

export function CMHFixture() {
  return (
    <group>
      <mesh><boxGeometry args={[1.7, 0.42, 1.2]} /><meshStandardMaterial color="#292524" metalness={0.7} roughness={0.35} /></mesh>
      <mesh position={[0, -0.26, 0]} rotation={[0, 0, Math.PI / 2]}><cylinderGeometry args={[0.11, 0.11, 0.9, 12]} /><meshStandardMaterial color="#fef3c7" emissive="#fcd34d" emissiveIntensity={2.4} /></mesh>
      <mesh position={[0, -1.8, 0]}><coneGeometry args={[1.5, 2.9, 24, 1, true]} /><meshBasicMaterial color="#fcd34d" transparent opacity={0.08} side={2} depthWrite={false} /></mesh>
      <HangWires spread={0.65} />
      <pointLight position={[0, -1.2, 0]} intensity={20} distance={11} color="#fcd34d" />
    </group>
  );
}

// ---- Photoreal layer: AI renders with removed backgrounds as in-scene billboards ----
import { Billboard, useTexture } from "@react-three/drei";

const CUTOUT = "/assets/growlab/cutout";

export function DevicePhoto({ id, width = 1.8 }) {
  const tex = useTexture(`${CUTOUT}/${id}.png`);
  const aspect = tex.image ? tex.image.height / tex.image.width : 0.55;
  return (
    <Billboard follow>
      <mesh>
        <planeGeometry args={[width, width * aspect]} />
        <meshStandardMaterial map={tex} transparent alphaTest={0.35} side={2} roughness={0.6} metalness={0.1} />
      </mesh>
    </Billboard>
  );
}

export function TreePhoto({ tree, running }) {
  const tex = useTexture(`${CUTOUT}/tree-${tree.name.toLowerCase()}.png`);
  const sway = useRef();
  useFrame(({ clock }) => {
    if (running && sway.current) sway.current.rotation.z = Math.sin(clock.getElapsedTime() * 0.7 + tree.pos[0]) * 0.02;
  });
  const h = tree.h + 1.2; // include pot + full canopy from the photo
  const w = h * 1.05;
  return (
    <group position={[tree.pos[0], SOIL_BASE, tree.pos[2]]}>
      <group ref={sway} position={[0, 0, 0]}>
        <group position={[0, h / 2 - 0.1, 0]}>
          <mesh>
            <planeGeometry args={[w, h]} />
            <meshStandardMaterial map={tex} transparent alphaTest={0.4} side={2} roughness={0.9} />
          </mesh>
          <mesh rotation={[0, Math.PI / 2, 0]}>
            <planeGeometry args={[w, h]} />
            <meshStandardMaterial map={tex} transparent alphaTest={0.4} side={2} roughness={0.9} />
          </mesh>
        </group>
      </group>
      <Html position={[0, h + 0.3, 0]} center distanceFactor={13} zIndexRange={[5, 0]}>
        <span className="font-mono text-[9px] tracking-widest text-emerald-300/80 bg-stone-950/70 px-1.5 py-0.5 rounded border border-emerald-500/20 whitespace-nowrap">
          {tree.name}
        </span>
      </Html>
    </group>
  );
}
